import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useMapLayer from '../../../hooks/UseMapLayer';
import config from '../../../config';
import {
  HOVERED_MARKETS_STYLE,
  HOVERED_ZIPS_STYLE,
  MARKETS_STYLE,
  ZIPS_STYLE,
} from '../../../utils/LeafletZipsLayer';
import {
  US_BBOX,
  ZIP_LAYER_DEFAULT_ZOOM,
  ZIP_LAYER_MIN_ZOOM,
} from '../../../utils/constants';

const ZIP_MAP_ID = 'addygroundtruth.maponics_zips';
const ZIP_MAPBOX_LAYER_ID = 'Maponics ZIP Boundaries';
const MARKET_MAP_ID = 'addygroundtruth.all_prodcodes';
const MARKET_MAPBOX_LAYER_ID = 'markets';

const ZIPS_LAYER_PARAMS = {
  idAttr: 'zip',
  mapId: ZIP_MAP_ID,
  layerId: ZIP_MAPBOX_LAYER_ID,
  style: ZIPS_STYLE,
  styleHovered: HOVERED_ZIPS_STYLE,
  minZoom: ZIP_LAYER_MIN_ZOOM,
};

const MARKETS_LAYER_PARAMS = {
  idAttr: 'prodcode',
  mapId: MARKET_MAP_ID,
  layerId: MARKET_MAPBOX_LAYER_ID,
  style: MARKETS_STYLE,
  styleHovered: HOVERED_MARKETS_STYLE,
  maxZoom: ZIP_LAYER_MIN_ZOOM - 1,
};

export default function MapWidget({
  mode, // 'touch' or 'hover'
  selectedZipcodes, // Record<zipcode, Demographics>
  demographics, // Demographics[]
  bbox, //{result?: [[number, number], [number, number]], limitZoom: boolean}
  availableMarkets, // Market[]
  hoveredZipcode,
  hoveredMarketId,
  onHoverZipcode, // (zipcode: number | null) => void
  onHoverMarket, // (prodcode: number | null) => void
  onSelectZipcode, // (zipcode: number, isSelected: boolean | undefined) => void ("isSelected === undefined" means toggle)
  onSelectMarket, // (prodcode: number) => void
  onZoomChange, //(zoom: number, latLng: {lat, lng}) => void
}) {
  const leafletRef = useRef(null);
  const [map, setMap] = useState(null);
  const [lng, setLng] = useState(-118.24);
  const [lat, setLat] = useState(34.05);
  const [zoom, setZoom] = useState(ZIP_LAYER_DEFAULT_ZOOM);

  const availableZipsByZipcode = useMemo(
    () =>
      (demographics || []).reduce(
        (res, zipcode) => res.set(zipcode.zip, zipcode),
        new Map(),
      ),
    [demographics],
  );

  const availableMarketsByProdcode = useMemo(
    () =>
      (availableMarkets || []).reduce(
        (res, market) => res.set(market.prodcode, market),
        new Map(),
      ),
    [availableMarkets],
  );

  const handleZoomChange = useCallback(() => {
    onZoomChange?.(map.getZoom(), map.getCenter());
  }, [onZoomChange, map]);

  const handleMouseLeave = useCallback(() => {
    if (mode === 'hover') {
      hoveredZipcode && onHoverZipcode(null);
      hoveredMarketId && onHoverMarket(null);
    }
  }, [mode, hoveredZipcode, hoveredMarketId, onHoverZipcode, onHoverMarket]);

  // create map
  useEffect(() => {
    const map = window.L.map(leafletRef.current, {
      maxZoom: 17,
      minZoom: 3,
      maxBounds: US_BBOX,
    });

    // Add zoom control on bottom left corner
    map.zoomControl.setPosition('bottomleft');
    setMap(map);

    /* Basemap */
    window.L.tileLayer(
      `https://api.mapbox.com/styles/v1/addygroundtruth/ckwqnhmpaa6zj14oc4ncuzn0k/tiles/256/{z}/{x}/{y}?access_token=${config.mapboxAccessToken}`,
      {
        center: [lng, lat],
        maxZoom: 18,
        attribution: false,
      },
    ).addTo(map);

    map.on('moveend', () => {
      const center = map.getCenter();
      setLng(center.lng.toFixed(4));
      setLat(center.lat.toFixed(4));
      setZoom(map.getZoom().toFixed(2));
    });

    return () => {
      map.remove();
    };
  }, []);

  // Monitor zoom change
  useEffect(() => {
    if (map) {
      map.on('zoomend', handleZoomChange);
      return () => {
        map.off('zoomend', handleZoomChange);
      };
    }
  }, [map, handleZoomChange]);

  // Zoom to bbox
  useEffect(() => {
    if (!map || !bbox?.result) {
      return;
    }

    // this is a workaround to set minZoom for "map.fitBounds()"
    const prevMinZoom = map.minZoom;
    if (bbox.limitZoom) {
      map.minZoom = ZIP_LAYER_DEFAULT_ZOOM;
    }
    map.fitBounds(bbox.result);
    map.minZoom = prevMinZoom;
  }, [map, bbox]);

  useMapLayer({
    map,
    mode,
    featuresById: availableZipsByZipcode,
    layerParams: ZIPS_LAYER_PARAMS,
    selectedFeatures: selectedZipcodes,
    hoveredFeatureId: hoveredZipcode,
    onSelectFeature: onSelectZipcode,
    onHoverFeature: onHoverZipcode,
  });

  useMapLayer({
    map,
    mode,
    featuresById: availableMarketsByProdcode,
    layerParams: MARKETS_LAYER_PARAMS,
    hoveredFeatureId: hoveredMarketId,
    onSelectFeature: onSelectMarket,
    onHoverFeature: onHoverMarket,
  });

  return (
    <div className="mapPanel">
      <div className="mapbox-sidebar">
        Longitude: {lng} | Latitude: {lat} | Zoom: {zoom}
      </div>
      <div
        ref={leafletRef}
        onMouseLeave={handleMouseLeave}
        style={{ height: '60vh' }}
      />
    </div>
  );
}
